/**
 * Funciones puras de calculo para KPIs financieros.
 *
 * Todas las funciones reciben datos ya cargados (no hacen queries)
 * y devuelven resultados calculados. Sin side effects.
 */

import { diffDays, toJSDate, computeGraceEnd } from "./interestCalculation";
import { AGING_BUCKETS } from "./kpiDefinitions";
import { esUnidadDelEdificio, esPagoDeAlquiler } from "./unidadesEdificio";

/**
 * Lo que cuenta para la morosidad de gastos comunes: sin las unidades del
 * edificio (portería, alquiler del edificio), sin sus pagos y sin los pagos de
 * alquiler (el alquiler es un ingreso del edificio, no un gasto común).
 */
function soloGastosComunes(payments = [], apartments = []) {
  const delEdificio = new Set((apartments || []).filter(esUnidadDelEdificio).map((a) => a.id));
  return {
    payments: (payments || []).filter((p) => !esPagoDeAlquiler(p) && !(p.apartmentId && delEdificio.has(p.apartmentId))),
    apartments: (apartments || []).filter((a) => !esUnidadDelEdificio(a)),
  };
}

// ─── Helpers internos ───────────────────────────────────────────

/**
 * Determina si un pago esta efectivamente impago.
 * Normaliza las variantes de status del codebase.
 */
function isUnpaid(payment) {
  if (payment.paid === true) return false;
  const st = (payment.status || "").toLowerCase();
  if (["pagado", "pagada", "aprobado", "aprobada"].includes(st)) return false;
  if (st === "rechazado" || st === "rechazada") return false;
  return true;
}

/**
 * Obtiene la fecha de vencimiento de un pago.
 * Si no tiene maturity, la reconstruye con month/year/paymentDueDay.
 */
function getMaturityDate(payment, paymentDueDay, building) {
  // ISO del vencimiento (string) para poder aplicar el período de gracia.
  let matIso = null;
  if (typeof payment.maturity === "string" && /^\d{4}-\d{2}-\d{2}/.test(payment.maturity)) {
    matIso = payment.maturity.slice(0, 10);
  } else {
    const mat = toJSDate(payment.maturity);
    if (mat) matIso = `${mat.getFullYear()}-${String(mat.getMonth() + 1).padStart(2, "0")}-${String(mat.getDate()).padStart(2, "0")}`;
  }
  if (!matIso) {
    const m = Number(payment.month);
    const y = Number(payment.year);
    if (m && y) matIso = `${y}-${String(m).padStart(2, "0")}-${String(paymentDueDay || 10).padStart(2, "0")}`;
  }
  if (!matIso) return null;
  // Con el building, la fecha efectiva de vencimiento (a partir de la cual hay
  // mora) es el FIN DE GRACIA — así quien paga dentro de la gracia no figura moroso.
  const effIso = building ? computeGraceEnd(matIso, building) : matIso;
  return toJSDate(effIso);
}

/**
 * Obtiene el monto pendiente de un pago.
 */
function getPendingAmount(payment) {
  if (payment.remainingAmount != null && payment.remainingAmount !== "") {
    return Number(payment.remainingAmount) || 0;
  }
  return Number(payment.amount) || 0;
}

// ─── KPI 1a: Tasa de Morosidad por Unidades ────────────────────

/**
 * Calcula la tasa de morosidad por unidades.
 *
 * @param {Array} payments - Todos los pagos del edificio
 * @param {Array} apartments - Todas las unidades del edificio
 * @param {number} [paymentDueDay=10] - Dia de vencimiento del edificio
 * @returns {{ rate: number, unitsOverdue: number, totalUnits: number }}
 */
export function calculateDelinquencyByUnits(paymentsAll, apartmentsAll, paymentDueDay = 10, building = null) {
  const { payments, apartments } = soloGastosComunes(paymentsAll, apartmentsAll);
  const now = new Date();
  const totalUnits = apartments.length;

  if (totalUnits === 0) {
    return { rate: 0, unitsOverdue: 0, totalUnits: 0 };
  }

  // Construir mapa de userid -> unitId para resolver pagos sin unitId
  const useridToUnitId = {};
  apartments.forEach((apt) => {
    const aptId = apt.id;
    if (apt.owner) useridToUnitId[String(apt.owner)] = aptId;
    if (apt.tenant) useridToUnitId[String(apt.tenant)] = aptId;
  });

  // Encontrar unidades con al menos 1 pago vencido e impago
  const unitsWithOverdue = new Set();

  payments.forEach((p) => {
    if (!isUnpaid(p)) return;

    const matDate = getMaturityDate(p, paymentDueDay, building);
    if (!matDate) return;

    const daysOver = diffDays(matDate, now);
    if (daysOver > 0) {
      // Resolver unidad: unitId directo, o mapear userid a unidad, o userid sintetico unit_XXX
      let unitKey = p.unitId;
      if (!unitKey && p.userid) {
        const uid = String(p.userid);
        if (uid.startsWith("unit_")) {
          unitKey = uid.replace("unit_", "");
        } else {
          unitKey = useridToUnitId[uid];
        }
      }
      if (unitKey) unitsWithOverdue.add(String(unitKey));
    }
  });

  const rate = (unitsWithOverdue.size / totalUnits) * 100;

  return {
    rate: Math.round(rate * 10) / 10,
    unitsOverdue: unitsWithOverdue.size,
    totalUnits,
  };
}

// ─── KPI 1b: Tasa de Morosidad por Monto ───────────────────────

/**
 * Calcula la tasa de morosidad por monto.
 *
 * @param {Array} payments - Todos los pagos del edificio
 * @param {number} [paymentDueDay=10]
 * @returns {{ rate: number, overdueAmount: number, totalAmount: number }}
 */
export function calculateDelinquencyByAmount(paymentsAll, paymentDueDay = 10, building = null) {
  const { payments } = soloGastosComunes(paymentsAll);
  const now = new Date();
  let overdueAmount = 0;
  let totalAmount = 0;

  payments.forEach((p) => {
    // Solo pagos generados por cierre de mes
    if (!p.montlyaccountId) return;

    const amt = Number(p.amount) || 0;
    totalAmount += amt;

    if (!isUnpaid(p)) return;

    const matDate = getMaturityDate(p, paymentDueDay, building);
    if (!matDate) return;

    if (diffDays(matDate, now) > 0) {
      overdueAmount += getPendingAmount(p);
    }
  });

  const rate = totalAmount > 0 ? (overdueAmount / totalAmount) * 100 : 0;

  return {
    rate: Math.round(rate * 10) / 10,
    overdueAmount,
    totalAmount,
  };
}

// ─── KPI 2: Aging de Deuda ──────────────────────────────────────

/**
 * Clasifica la deuda vencida en buckets temporales.
 * Cada unidad se clasifica por su pago MAS ANTIGUO impago.
 *
 * @param {Array} payments - Todos los pagos del edificio
 * @param {Array} apartments - Todas las unidades
 * @param {number} [paymentDueDay=10]
 * @returns {{ buckets: Array<{ key, label, color, units: number, amount: number, percentage: number }>, totalOverdue: number }}
 */
export function calculateAgingBuckets(paymentsAll, apartmentsAll, paymentDueDay = 10, building = null) {
  const { payments } = soloGastosComunes(paymentsAll, apartmentsAll);
  const now = new Date();

  // Agrupar pagos vencidos por unidad
  const unitDebt = {}; // { unitKey: { maxDays, totalAmount } }

  payments.forEach((p) => {
    if (!isUnpaid(p)) return;

    const matDate = getMaturityDate(p, paymentDueDay, building);
    if (!matDate) return;

    const daysOver = diffDays(matDate, now);
    if (daysOver <= 0) return;

    const unitKey = p.unitId || p.userid;
    if (!unitKey) return;
    const key = String(unitKey);

    if (!unitDebt[key]) {
      unitDebt[key] = { maxDays: 0, totalAmount: 0 };
    }

    const pending = getPendingAmount(p);
    unitDebt[key].totalAmount += pending;

    // La unidad va al bucket de su pago MAS ANTIGUO
    if (daysOver > unitDebt[key].maxDays) {
      unitDebt[key].maxDays = daysOver;
    }
  });

  // Calcular totales por bucket
  const totalOverdue = Object.values(unitDebt).reduce((s, u) => s + u.totalAmount, 0);

  const buckets = AGING_BUCKETS.map((bucket) => {
    let units = 0;
    let amount = 0;

    Object.values(unitDebt).forEach((u) => {
      if (u.maxDays >= bucket.min && u.maxDays <= bucket.max) {
        units++;
        amount += u.totalAmount;
      }
    });

    return {
      key: bucket.key,
      label: bucket.label,
      color: bucket.color,
      colorLight: bucket.colorLight,
      units,
      amount: Math.round(amount),
      percentage: totalOverdue > 0 ? Math.round((amount / totalOverdue) * 100) : 0,
    };
  });

  return { buckets, totalOverdue: Math.round(totalOverdue) };
}

// ─── KPI 3: CEI (Collection Effectiveness Index) ────────────────

/**
 * Calcula el CEI a partir de snapshots mensuales.
 *
 * @param {Object} snapshotPrev - Snapshot del mes anterior (puede ser null)
 * @param {Object} snapshotCurrent - Snapshot del mes actual
 * @returns {{ cei: number | null, details: Object }}
 */
export function calculateCEI(snapshotPrev, snapshotCurrent) {
  if (!snapshotCurrent) {
    return { cei: null, details: { error: "Sin snapshot del mes actual" } };
  }

  const cxcInicio = snapshotPrev ? snapshotPrev.totalCxC : 0;
  const liquidaciones = snapshotCurrent.totalLiquidaciones || 0;
  const cxcFinalTotal = snapshotCurrent.totalCxC || 0;
  const cxcFinalCorriente = snapshotCurrent.totalCxCCurrent || 0;

  const numerator = cxcInicio + liquidaciones - cxcFinalTotal;
  const denominator = cxcInicio + liquidaciones - cxcFinalCorriente;

  if (denominator <= 0) {
    return { cei: null, details: { error: "Denominador <= 0" } };
  }

  const cei = (numerator / denominator) * 100;

  return {
    cei: Math.round(Math.min(cei, 100) * 10) / 10,
    details: { cxcInicio, liquidaciones, cxcFinalTotal, cxcFinalCorriente },
  };
}

// ─── KPI 4: Concentracion de Mora ───────────────────────────────

/**
 * Calcula el ratio de concentracion de mora (Pareto).
 *
 * @param {Array} payments - Todos los pagos del edificio
 * @param {number} [paymentDueDay=10]
 * @returns {{ top20Ratio: number, top20Count: number, totalDebtors: number, debtors: Array, unitsFor80Pct: number }}
 */
export function calculateConcentrationRatio(paymentsAll, paymentDueDay = 10, building = null) {
  const { payments } = soloGastosComunes(paymentsAll);
  const now = new Date();

  // Agrupar deuda vencida por unidad
  const unitDebts = {};

  payments.forEach((p) => {
    if (!isUnpaid(p)) return;

    const matDate = getMaturityDate(p, paymentDueDay, building);
    if (!matDate) return;
    if (diffDays(matDate, now) <= 0) return;

    const unitKey = String(p.unitId || p.userid || "unknown");
    if (!unitDebts[unitKey]) {
      unitDebts[unitKey] = { unitKey, totalDebt: 0, overdueMonths: 0, payments: [] };
    }
    unitDebts[unitKey].totalDebt += getPendingAmount(p);
    unitDebts[unitKey].overdueMonths++;
    unitDebts[unitKey].payments.push(p);
  });

  const debtors = Object.values(unitDebts)
    .filter((d) => d.totalDebt > 0)
    .sort((a, b) => b.totalDebt - a.totalDebt);

  const totalDebt = debtors.reduce((s, d) => s + d.totalDebt, 0);
  const totalDebtors = debtors.length;

  if (totalDebtors === 0) {
    return { top20Ratio: 0, top20Count: 0, totalDebtors: 0, debtors: [], unitsFor80Pct: 0 };
  }

  // Top 20%
  const top20Count = Math.max(1, Math.ceil(totalDebtors * 0.2));
  const top20Debt = debtors.slice(0, top20Count).reduce((s, d) => s + d.totalDebt, 0);
  const top20Ratio = totalDebt > 0 ? (top20Debt / totalDebt) * 100 : 0;

  // Unidades para cubrir 80% de la deuda (Pareto)
  let accumulated = 0;
  let unitsFor80Pct = 0;
  for (const d of debtors) {
    accumulated += d.totalDebt;
    unitsFor80Pct++;
    if (accumulated >= totalDebt * 0.8) break;
  }

  // Clasificar deudores
  const classified = debtors.map((d) => ({
    ...d,
    totalDebt: Math.round(d.totalDebt),
    classification: classifyDebtor(d.payments, paymentDueDay, building),
  }));

  return {
    top20Ratio: Math.round(top20Ratio * 10) / 10,
    top20Count,
    totalDebtors,
    debtors: classified,
    unitsFor80Pct,
  };
}

/**
 * Clasifica un deudor segun su patron de atrasos.
 */
function classifyDebtor(overduePayments, paymentDueDay, building = null) {
  if (overduePayments.length >= 6) return "estructural";

  // Verificar si hay patron de recurrencia en ultimos 6 meses
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  const recentOverdue = overduePayments.filter((p) => {
    const mat = getMaturityDate(p, paymentDueDay, building);
    return mat && mat >= sixMonthsAgo;
  });

  if (recentOverdue.length >= 3) return "recurrente";

  return "circunstancial";
}

// ─── KPI 5: Ratio de Gastos Operativos ──────────────────────────

/**
 * Calcula el ratio de gastos operativos.
 *
 * @param {number} totalExpenses - Gastos operativos totales del periodo
 * @param {number} totalIncome - Ingresos totales del periodo
 * @returns {{ ratio: number }}
 */
export function calculateOperatingRatio(totalExpenses, totalIncome) {
  if (totalIncome <= 0) {
    return { ratio: totalExpenses > 0 ? 100 : 0 };
  }
  const ratio = (totalExpenses / totalIncome) * 100;
  return { ratio: Math.round(ratio * 10) / 10 };
}

// ─── KPI 7: Meses de Reserva ───────────────────────────────────

/**
 * Calcula los meses de reserva.
 *
 * @param {number} reserveBalance - Saldo actual del fondo de reserva
 * @param {number} avgMonthlyExpenses - Gasto mensual promedio (ultimos 3 meses)
 * @returns {{ months: number }}
 */
export function calculateReserveMonths(reserveBalance, avgMonthlyExpenses) {
  if (avgMonthlyExpenses <= 0) {
    return { months: reserveBalance > 0 ? 99 : 0 };
  }
  const months = reserveBalance / avgMonthlyExpenses;
  return { months: Math.round(months * 10) / 10 };
}
